/**
 * ChestCflBonusIndicator Component
 *
 * Displays a hoverable "2x POINTS" indicator on the Chest page for CFL (Crypto Fantasy League) whitelisted users
 * Shows campaign details on hover including:
 * - Time remaining
 * - Points progress (X/20,000)
 * - Whitelist eligibility status
 *
 * Positioned above the chest slider bar, left-aligned
 */

import { useState } from 'react';

import { useCflCampaign } from '@/hooks/useCflCampaign';

// CFL brand color (green)
const CFL_COLOR = '#2ECC71';

function formatTimeRemaining(endDate: string): string {
  const now = new Date();
  const end = new Date(endDate);
  const diff = end.getTime() - now.getTime();

  if (diff <= 0) {
    return 'Campaign ended';
  }

  const days = Math.floor(diff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  if (hours > 0) {
    return `${hours}h ${minutes}m left`;
  }
  return `${minutes}m left`;
}

export function ChestCflBonusIndicator() {
  const { campaignData, isLoading } = useCflCampaign();
  const [isHovered, setIsHovered] = useState(false);

  // Only show if user is CFL whitelisted and hasn't reached the cap
  if (isLoading || !campaignData?.isActive || !campaignData?.isCflWhitelisted || campaignData?.remainingBonus <= 0) {
    return null;
  }

  const isEligible = campaignData.isEligible && campaignData.remainingBonus > 0;
  const timeRemaining = formatTimeRemaining(campaignData.endDate);
  const progress = `${campaignData.userBonusEarned.toLocaleString()}/${campaignData.maxBonus.toLocaleString()}`;

  return (
    <div
      className="relative inline-flex"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* CFL 2x Badge */}
      <div
        className="cursor-pointer transition-all duration-200 flex items-center gap-2 px-3 py-1.5 rounded-full"
        style={{
          transform: isHovered ? 'scale(1.05)' : 'scale(1)',
          backgroundColor: 'rgba(46, 204, 113, 0.15)',
          border: `1px solid ${CFL_COLOR}`,
        }}
      >
        <img
          src="/cfl-logo.png"
          alt="CFL"
          className="w-4 h-4 object-contain"
        />
        <span
          className="text-sm font-bold"
          style={{ color: CFL_COLOR, fontFamily: 'Outfit, sans-serif' }}
        >
          CFL 2x
        </span>
      </div>

      {/* Hover Tooltip */}
      {isHovered && (
        <div
          className="absolute top-full left-0 mt-2 w-72 p-4 rounded-xl shadow-2xl z-50 animate-slide-in"
          style={{
            backgroundColor: '#343434',
            border: `2px solid ${CFL_COLOR}`,
            boxShadow: `0 0 20px rgba(46, 204, 113, 0.3)`,
          }}
        >
          {/* Header */}
          <div className="flex items-center gap-2 mb-3">
            <img
              src="/cfl-logo.png"
              alt="CFL"
              className="w-5 h-5 object-contain"
            />
            <h3
              className="text-base font-bold"
              style={{ color: CFL_COLOR, fontFamily: 'Outfit, sans-serif' }}
            >
              Crypto Fantasy League
            </h3>
          </div>

          {/* Content */}
          <div className="space-y-3">
            {isEligible ? (
              <>
                {/* For eligible users */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  You're earning <strong style={{ color: CFL_COLOR }}>2x points</strong> on every chest opening!
                </p>

                {/* Progress Bar */}
                <div>
                  <div className="flex justify-between text-xs mb-1" style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>
                    <span>Bonus Progress</span>
                    <span style={{ color: CFL_COLOR, fontWeight: 600 }}>{progress} pts</span>
                  </div>
                  <div className="w-full h-2 rounded-full" style={{ backgroundColor: 'rgba(46, 204, 113, 0.2)' }}>
                    <div
                      className="h-full rounded-full transition-all duration-500"
                      style={{
                        width: `${(campaignData.userBonusEarned / campaignData.maxBonus) * 100}%`,
                        backgroundColor: CFL_COLOR,
                      }}
                    />
                  </div>
                </div>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: `1px solid rgba(46, 204, 113, 0.3)` }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Campaign ends in</span>
                  <span style={{ color: CFL_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* For non-eligible users (shouldn't display since we filter above) */}
                <p className="text-sm" style={{ color: 'white', fontFamily: 'Outfit, sans-serif' }}>
                  <strong style={{ color: CFL_COLOR }}>CFL members</strong> earn 2x points on every chest opening!
                </p>

                {/* Time Remaining */}
                <div className="flex justify-between items-center text-xs pt-2" style={{ borderTop: `1px solid rgba(46, 204, 113, 0.3)` }}>
                  <span style={{ color: '#999', fontFamily: 'Outfit, sans-serif' }}>Offer ends in</span>
                  <span style={{ color: CFL_COLOR, fontWeight: 600, fontFamily: 'Outfit, sans-serif' }}>
                    {timeRemaining}
                  </span>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
